import type { FormEvent } from 'react'
import { formNodes, type FlowKind, type KratosFlow } from '../api/flows'
import { useI18n } from '../i18n'
import { translateKratosFieldLabel, translateKratosMessage } from '../i18n/kratos'

interface KratosFormProps {
  flow: KratosFlow
  group: string
  kind: FlowKind
  busy: boolean
  submitLabel: string
  onSubmit: (data: Record<string, string>) => void
}

export function KratosForm({ flow, group, kind, busy, submitLabel, onSubmit }: KratosFormProps) {
  const { t } = useI18n()
  const nodes = formNodes(flow, group, kind)
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (busy) return
    const data = new FormData(event.currentTarget)
    const submitter = (event.nativeEvent as SubmitEvent).submitter
    if (submitter instanceof HTMLButtonElement && submitter.name) {
      data.set(submitter.name, submitter.value)
    }
    if (!data.has('method')) data.set('method', group)
    const values: Record<string, string> = {}
    data.forEach((value, name) => { values[name] = String(value) })
    onSubmit(values)
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit} aria-busy={busy}>
      {nodes.map((node, index) => {
        const attrs = node.attributes
        const key = `${group}-${attrs.name || node.type}-${index}`
        if (node.type === 'text') {
          return <p key={key} className="text-sm text-gray-600">{attrs.text && translateKratosMessage(attrs.text, t)}</p>
        }
        if (attrs.type === 'hidden') {
          return <input key={key} type="hidden" name={attrs.name} value={String(attrs.value ?? '')} disabled={attrs.disabled} />
        }
        if (attrs.type === 'submit') {
          let label = submitLabel
          if (attrs.name !== 'method' && node.meta?.label) label = translateKratosMessage(node.meta.label, t)
          if (busy) label = t('Please wait…')
          return (
            <button key={key} type="submit" name={attrs.name} value={String(attrs.value ?? '')}
              disabled={busy || attrs.disabled} formNoValidate={attrs.name === 'email' || attrs.name === 'resend'}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              {busy && <span className="auth-spinner" aria-hidden="true" />}
              {label}
            </button>
          )
        }
        const id = `${group}-${attrs.name}`
        const isEmail = attrs.name === 'identifier' || attrs.name === 'traits.email' || attrs.name === 'email'
        const isCode = attrs.name === 'code'
        const label = translateKratosFieldLabel(attrs.name, node.meta?.label, t)
        let autocomplete = attrs.autocomplete
        if (!autocomplete) {
          if (attrs.type === 'password') autocomplete = kind === 'login' ? 'current-password' : 'new-password'
          else if (isEmail) autocomplete = kind === 'login' ? 'username' : 'email'
          else if (isCode) autocomplete = 'one-time-code'
        }
        return (
          <div key={key}>
            <label htmlFor={id} className="label">{label}</label>
            <input id={id} name={attrs.name} type={attrs.type || 'text'}
              required={attrs.required} disabled={busy || attrs.disabled}
              readOnly={attrs.readonly}
              autoComplete={autocomplete} inputMode={isEmail ? 'email' : undefined}
              autoCapitalize={isEmail || isCode ? 'none' : undefined} spellCheck={isEmail || isCode ? false : undefined}
              aria-invalid={node.messages?.some(message => message.type === 'error') || undefined}
              defaultValue={attrs.type === 'password' ? '' : String(attrs.value ?? '')}
              className="input mt-2" aria-describedby={node.messages?.length ? `${id}-messages` : undefined} />
            {Boolean(node.messages?.length) && <div id={`${id}-messages`}>
              {node.messages?.map((message, messageIndex) => (
                <p key={`${message.id}-${messageIndex}`} className="mt-1 text-sm text-red-600">{translateKratosMessage(message, t)}</p>
              ))}
            </div>}
          </div>
        )
      })}
      {!nodes.some(node => node.attributes.type === 'submit') && (
        <button type="submit" disabled={busy} className="w-full btn-primary disabled:opacity-50">
          {busy && <span className="auth-spinner" aria-hidden="true" />}
          {busy ? t('Please wait…') : submitLabel}
        </button>
      )}
    </form>
  )
}
